/**
 * Orders API
 * CRUD operations for orders (Aufträge)
 */

import {backendClient, handleSupabaseError, supabase} from './client'
import {ListOrder, Order, StateColor} from '@/types/Order'
import {AssetFetchParams, AssetFetchResult} from "@/types/Asset";
import {applyFilter, getRowsFiltered, ListParams, ListResponse} from "@/api/index";
import type {Drive} from "@/types/Drive";

// Additional types for API responses
export interface OrderWithProgress extends Order {
    progress: number
    task_count: number
    completed_task_count: number
}

export interface OrderFilters {
    state?: string
    workGroup?: string
    author?: string

    [key: string]: any
}

/**
 * Get single order by ID with tasks
 */
export async function getOrder(id: number): Promise<Order | null> {
    const {data, error} = await supabase
        .from('orders')
        .select(`
      *,
      tasks (*)
    `)
        .eq('id', id)
        .single()

    if (error) {
        if (error.code === 'PGRST116') return null // Not found
        handleSupabaseError(error)
    }

    return data
}

/**
 * Get orders with progress (uses view)
 */
export async function getOrdersWithProgress(): Promise<OrderWithProgress[]> {
    const {data, error} = await supabase
        .from('orders_with_progress')
        .select('*')
        .order('scheduled_start', {ascending: false})

    if (error) handleSupabaseError(error)
    return data || []
}

/**
 * Get paginated and filtered orders
 */
export async function getOrdersFiltered(params: ListParams): Promise<ListResponse<ListOrder>> {
    return getRowsFiltered(params, "orders", "id, title, author, asset_id, state, scheduled_duration, scheduled_start")
}

/**
 * Get order titles for search/autocomplete
 */

/*export async function getOrderTitles(): Promise<{ id: number; title: string }[]> {
  const { data, error } = await supabase
    .from('orders')
    .select('id, title')
    .order('id', { ascending: false })

  if (error) handleSupabaseError(error)
  return data || []
}*/

/*export async function getCompressedOrders(params: AssetFetchParams): Promise<AssetFetchResult> {
    const {
        offset = 0,
        limit = 100,
        search = ""
    } = params;

    let query = supabase
        .from('orders')
        .select('id, title, state', {count: 'exact'})
        .order('id', {ascending: true})
        .range(offset, offset + limit - 1)

    if (search) {
        query = query.or(`id.ilike.%${search}%,title.ilike.%${search}%`)
    }

    const {data, count} = await query
    return {
        items: (data || []).map(d => ({
            title: d.title,
            subtitle: d.id || '',
            chip: d.state,
        })),
        total: count || 0,
    }
}*/

export async function getCompressedOrders(params: AssetFetchParams, baseFilter: Record<string, any>): Promise<AssetFetchResult> {
    const {
        offset = 0,
        limit = 100,
        search = ""
    } = params;

    const {data, error} = await supabase.rpc('filter_rows', {
        p_table: "orders",
        p_filters: baseFilter,
        p_sort_column: 'id',
        p_sort_order: 'asc',
        p_limit: limit,
        p_offset: offset,
        p_select: "id, title, state",
        p_search: search,
        p_search_columns: ['id', 'title']
    })

    if (error) throw error

    return {
        items: (data?.items || []).map((d: any) => ({
            title: d.id,
            subtitle: d.title || '',
            chip: d.state,
            chip_color: `${d.state.toString().toLowerCase()}_color`,
            id: d.id,
        })),
        total: data?.total || 0,
    }
}

/**
 * Create new order
 */
export async function createOrder(order: Partial<Order>): Promise<Order> {
    const {data, error} = await supabase
        .from('orders')
        .insert(order)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

/**
 * Update order
 */
export async function updateOrder(id: number, updates: Partial<Order>): Promise<Order> {
    const {data, error} = await supabase
        .from('orders')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

/**
 * Delete order
 */
export async function deleteOrder(id: number): Promise<void> {
    const {error} = await supabase
        .from('orders')
        .delete()
        .eq('id', id)

    if (error) handleSupabaseError(error)
}
